import {
  AlertType,
  DismissedAlert,
  Tkv2OrderAttachmentBody,
  dismissedAlertMatches,
  isDefined,
  nonDismissibleAlertTypes,
  toDismissedAlertEntry,
} from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { AlertsState, SyncResult } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { removeComp, removeComps } from './actions/removeComp.ts';
import { getBumpedSyncState } from './utils/getBumpedSyncState.ts';

export type DismissAlertPayload = {
  type: AlertType;
  propertyID?: string;
  targetCellKey?: string;
};

const filterDismissedAlertsForRemovedProperties = (
  dismissedAlerts: DismissedAlert[],
  propertyIds: readonly string[],
): DismissedAlert[] =>
  dismissedAlerts.filter(
    (entry) => typeof entry === 'string' || !propertyIds.includes(entry.propertyID),
  );

export const initialAlertsState: AlertsState = {
  dismissedAlerts: [],
};

const alertsSlice = createSlice({
  name: 'alerts',
  initialState: initialAlertsState,
  reducers: {
    dismissAlert: (state, action: PayloadAction<DismissAlertPayload>) => {
      if (nonDismissibleAlertTypes.includes(action.payload.type)) return;
      const entry = toDismissedAlertEntry(action.payload);
      const { type, propertyID, targetCellKey } = action.payload;
      const withoutSupersededPropertyLevel =
        targetCellKey && propertyID
          ? state.dismissedAlerts.filter(
              (dismissed) =>
                typeof dismissed === 'string' ||
                dismissed.type !== type ||
                dismissed.propertyID !== propertyID ||
                Boolean(dismissed.targetCellKey),
            )
          : state.dismissedAlerts;
      if (
        withoutSupersededPropertyLevel.some((dismissed) =>
          dismissedAlertMatches(dismissed, action.payload),
        )
      ) {
        return;
      }

      Object.assign(state, {
        dismissedAlerts: [...withoutSupersededPropertyLevel, entry],
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    cancelDismissAlert: (state, action: PayloadAction<DismissAlertPayload>) => {
      if (!state.dismissedAlerts.some((dismissed) => dismissedAlertMatches(dismissed, action.payload)))
        return;

      Object.assign(state, {
        dismissedAlerts: state.dismissedAlerts.filter(
          (dismissed) => !dismissedAlertMatches(dismissed, action.payload),
        ),
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    setAlerts: (state, action: PayloadAction<Tkv2OrderAttachmentBody<'alerts'>>) => {
      Object.assign(state, {
        ...action.payload,
        syncState: state.syncState ?? {
          editRevision: 0,
          serverRevision: 0,
          status: 'success' as const,
        },
      });
    },
    alertsMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.syncState)) {
        Object.assign(state.syncState, { status });

        if (status === 'success') {
          Object.assign(state.syncState, { serverRevision: payload.serverRevision });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialAlertsState);
    builder.addCase(removeComp, (state, action) => {
      const dismissedAlerts = filterDismissedAlertsForRemovedProperties(state.dismissedAlerts, [
        action.payload,
      ]);
      if (dismissedAlerts.length === state.dismissedAlerts.length) return;
      Object.assign(state, {
        dismissedAlerts,
        syncState: getBumpedSyncState(state.syncState),
      });
    });
    builder.addCase(removeComps, (state, action) => {
      const dismissedAlerts = filterDismissedAlertsForRemovedProperties(
        state.dismissedAlerts,
        action.payload.ids,
      );
      if (dismissedAlerts.length === state.dismissedAlerts.length) return;
      Object.assign(state, {
        dismissedAlerts,
        syncState: getBumpedSyncState(state.syncState),
      });
    });
  },
});

export const { dismissAlert, cancelDismissAlert, setAlerts, alertsMutationComplete } =
  alertsSlice.actions;

export const alertsReducer = alertsSlice.reducer;
